import React, { useState, useRef, useEffect } from 'react';
import styles from './YoutubeStylePlayer.module.css';

const YoutubeStylePlayer = ({ videoSrc, posterSrc, className }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1); // Громкость от 0 до 1
  const [isMuted, setIsMuted] = useState(false); // Флаг отключения звука
  const [isFullscreen, setIsFullscreen] = useState(false); // Флаг полноэкранного режима
  
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const volumeSliderRef = useRef(null);
  
  // Установка начальной громкости при монтировании
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
    
    // Добавление слушателя событий для отслеживания выхода из полноэкранного режима
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.mozFullScreenElement || 
        document.msFullscreenElement
      );
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      // Очистка слушателей при размонтировании
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);
  
  // Функция форматирования времени из секунд в мм:сс
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  // Обработчик воспроизведения/паузы
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  // Обработчик обновления времени
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      
      setCurrentTime(current);
      setProgress((current / duration) * 100);
    }
  };
  
  // Обработчик загрузки метаданных видео
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };
  
  // Обработчик клика по прогресс-бару для перемотки
  const handleProgressClick = (e) => {
    if (progressBarRef.current && videoRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };
  
  // Обработчик клика по ползунку громкости
  const handleVolumeClick = (e) => {
    if (volumeSliderRef.current && videoRef.current) {
      const rect = volumeSliderRef.current.getBoundingClientRect();
      const newVolume = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    }
  };
  
  // Обработчик нажатия на иконку громкости (mute/unmute)
  const handleMuteToggle = () => {
    if (videoRef.current) {
      if (!isMuted) {
        // Запоминаем текущую громкость перед отключением
        videoRef.current.muted = true;
        setIsMuted(true);
      } else {
        videoRef.current.muted = false;
        setIsMuted(false);
        if (volume === 0) {
          // Если громкость была 0, устанавливаем значение по умолчанию
          const defaultVolume = 0.5;
          videoRef.current.volume = defaultVolume;
          setVolume(defaultVolume);
        }
      }
    }
  };
  
  // Функция для определения иконки громкости в зависимости от уровня
  const getVolumeIcon = () => {
    if (isMuted || volume === 0) {
      return (
        <svg className={styles.volumeIcon} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.34 2.93L2.93 4.34L7.29 8.7L7 9H3v6h4l5 5v-6.59l4.18 4.18c-.65.49-1.38.88-2.18 1.11v2.06c1.34-.3 2.57-.92 3.61-1.75l2.05 2.05l1.41-1.41L4.34 2.93zM10 15.17L7.83 13H5v-2h2.83l.88-.88L10 11.41v3.76zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87c0-4.28-3.01-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zm-7-8l-1.88 1.88L12 7.76zm4.5 8c0-1.77-1.02-3.29-2.5-4.03v1.79l2.48 2.48c.01-.08.02-.16.02-.24z" fill="currentColor" />
        </svg>
      );
    } else if (volume < 0.5) {
      return (
        <svg className={styles.volumeIcon} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" fill="currentColor" />
        </svg>
      );
    } else {
      return (
        <svg className={styles.volumeIcon} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor" />
        </svg>
      );
    }
  };
  
  // Обработчик полноэкранного режима
  const handleFullscreen = () => {
    if (!containerRef.current) return;
    
    // Включение полноэкранного режима
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.mozRequestFullScreen) {
        containerRef.current.mozRequestFullScreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } 
    // Выключение полноэкранного режима
    else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`${styles.videoContainer} ${className || ''} ${isFullscreen ? styles.fullscreen : ''}`}
      style={{ 
        width: '100%', 
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Кастомное превью с кнопкой воспроизведения */}
      {!isPlaying && (
        <div 
          className={styles.videoCover} 
          onClick={handlePlayPause}
          style={posterSrc ? { 
            backgroundImage: `url(${posterSrc})`,
            backgroundSize: '120%',
            backgroundPosition: 'center',
            width: '100%',
            height: '100%'
          } : {
            width: '100%',
            height: '100%'
          }}
        >
          <div className={styles.playButtonWrapper}>
            <div className={styles.playButton}>
              <svg 
                className={styles.playButtonIcon} 
                viewBox="0 0 24 24" 
                fill="white" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
              </svg>
            </div>
          </div>
        </div>
      )}
      
      {/* Видеоплеер */}
      <video 
        ref={videoRef}
        className={styles.video}
        src={videoSrc}
        controls={false} 
        poster={posterSrc}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onEnded={() => setIsPlaying(false)}
        onClick={handlePlayPause}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
      />
      
      {/* Элементы управления в стиле YouTube */}
      <div className={`${styles.videoControls} ${isPlaying ? styles.showControls : ''}`}>
        <div 
          ref={progressBarRef}
          className={styles.progressBar}
          onClick={handleProgressClick}
        >
          <div 
            className={styles.progress} 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <div className={styles.controlsBottom}>
          <div className={styles.leftControls}>
            <button className={styles.controlButton} onClick={handlePlayPause}>
              {isPlaying ? (
                <svg className={styles.controlIcon} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" />
                  <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" />
                </svg>
              ) : (
                <svg className={styles.controlIcon} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
                </svg>
              )}
            </button>
            
            <div className={styles.volume}>
              <button className={styles.volumeButton} onClick={handleMuteToggle}>
                {getVolumeIcon()}
              </button>
              <div 
                ref={volumeSliderRef}
                className={styles.volumeSlider} 
                onClick={handleVolumeClick}
              >
                <div 
                  className={styles.volumeLevel}
                  style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
                ></div>
              </div>
            </div>
            
            <div className={styles.time}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
          
          <div className={styles.rightControls}>
            <button className={styles.fullscreenButton} onClick={handleFullscreen}>
              {isFullscreen ? (
                <svg className={styles.controlIcon} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" fill="currentColor" />
                </svg>
              ) : (
                <svg className={styles.controlIcon} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 14H5V19H10V17H7V14Z" fill="currentColor" />
                  <path d="M5 10H7V7H10V5H5V10Z" fill="currentColor" />
                  <path d="M17 17H14V19H19V14H17V17Z" fill="currentColor" />
                  <path d="M14 5V7H17V10H19V5H14Z" fill="currentColor" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubeStylePlayer; 